import Select, { components } from 'react-select'
import { selectStyles } from '@astro/astrologer-forms'
import { COUNTRY_SELECT_OPTIONS, matchesCountryQuery } from '../../data/countries'

/* ── Flag + dial-code picker that sits inside a phone field's left edge. ──
   Reuses the shared react-select skin, flattened to a borderless chip so it
   reads as part of the input rather than a second control. */

const inlineSelectStyles = {
  ...selectStyles,
  control: (base) => ({
    ...base,
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    minHeight: 0,
    boxShadow: 'none',
    cursor: 'pointer',
    ':hover': { borderColor: 'transparent' },
  }),
  valueContainer: (base) => ({ ...base, padding: 0 }),
  singleValue: (base) => ({ ...base, margin: 0, color: '#1c0a04' }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: '#1c0a04' }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 2px',
    color: '#b45309',
    ':hover': { color: '#92400e' },
  }),
  // Wider than its 104px trigger so full country names stay readable.
  menu: (base) => ({ ...selectStyles.menu(base), width: 320, marginTop: 12 }),
}

const CountryFlag = ({ src, className = '' }) => (
  <img
    src={src}
    alt=""
    width={20}
    height={14}
    loading="lazy"
    className={`w-5 h-3.5 shrink-0 rounded-[2px] object-cover ring-1 ring-black/5 ${className}`}
  />
)

const Option = (props) => (
  <components.Option {...props}>
    <span className="flex items-center gap-2.5">
      <CountryFlag src={props.data.flag} />
      <span className="truncate">{props.data.label}</span>
      <span className={`ml-auto tabular-nums ${props.isSelected ? 'text-white/80' : 'text-stone-400'}`}>
        {props.data.dialCode}
      </span>
    </span>
  </components.Option>
)

const SingleValue = ({ children: _children, ...props }) => (
  <components.SingleValue {...props}>
    <span className="flex items-center gap-1.5">
      <CountryFlag src={props.data.flag} />
      <span className="text-sm font-semibold text-stone-600 tabular-nums">{props.data.dialCode}</span>
    </span>
  </components.SingleValue>
)

/**
 * @param {object} props
 * @param {string} props.value - selected ISO alpha-2 code, e.g. 'IN'
 * @param {(iso2: string) => void} props.onChange
 * @param {string} [props.inputId] - unique when more than one sits on a page
 */
export default function CountryCodeSelect({ value, onChange, inputId = 'country-dial-code' }) {
  return (
    <div className="absolute left-3 z-20 w-[104px] border-r border-stone-300/70 pr-2">
      <Select
        inputId={inputId}
        aria-label="Country dial code"
        options={COUNTRY_SELECT_OPTIONS}
        value={COUNTRY_SELECT_OPTIONS.find((option) => option.value === value) ?? null}
        onChange={(option) => onChange(option?.value ?? '')}
        components={{ Option, SingleValue }}
        filterOption={(option, input) => matchesCountryQuery(option.data.country, input)}
        styles={inlineSelectStyles}
        placeholder=""
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </div>
  )
}
